<template>
  <li class="owc-footer-sitemap-section">
    <button class="owc-footer-sitemap-section__title-wrapper" @click="open">
      <h5 class="owc-footer-sitemap-section__title">
        {{ title }}
      </h5>
      <div
        class="owc-footer-sitemap-section__expand-icon-wrapper"
        :class="{
          'owc-footer-sitemap-section__expand-icon-wrapper--expanded': visible,
        }"
      >
        <wb-icon class="owc-footer-sitemap-section__expand-icon" name="chevron-down" />
      </div>
    </button>
    <transition name="accordion" @enter="start" @after-enter="end" @before-leave="start" @after-leave="end">
      <div class="owc-footer-sitemap-section__link-list" v-show="visible || !isMobile">
        <!-- Transitions on the accordion cannot have padding, this is why we use this wrapper to add the padding -->
        <ul class="owc-footer-sitemap-section__link-list-wrapper">
          <li class="owc-footer-sitemap-section__link-wrapper" v-for="(item, index) in links" :key="index">
            <a
              class="owc-footer-sitemap-section__link"
              :href="item.url"
              :target="item.target"
              data-track="false"
              @click="handleTracking(item.url, item.label)"
              >{{ item.label }}</a
            >
          </li>
        </ul>
      </div>
    </transition>
  </li>
</template>

<script lang="ts">
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';

import {
  SiteMapSectionData,
  SiteMapSectionMethods,
  SiteMapSectionComputed,
  SiteMapSectionProps,
} from './SiteMapSection';
import {
  MediaQueryService,
  WB_RESIZE_EVENT_NAME,
  mobileBreakpoints,
  tabletBreakpoints,
} from '@owc-utils/MediaQueryService';
// Importing MediaQueryChangeEvent type declaration
import { MediaQueryChangeEvent } from '@owc-utils/MediaQueryService/MediaQueryChangeEvent';
import { componentMode } from './Footer.config';

/**
 * `SiteMapSection Footer` - SiteMapSection for OneWeb - This component contains the sitemap of the website, splited by topics.
 */

export default ComponentInitializeDispatcher.extend<
  SiteMapSectionData,
  SiteMapSectionMethods,
  SiteMapSectionComputed,
  SiteMapSectionProps
>({
  name: 'owc-site-map-section',
  props: {
    siteMapSection: {
      type: Object,
    },
  },
  data() {
    return {
      active: false,
      wbMediaQueryService: new MediaQueryService(window),
      isMobile: true,
    };
  },
  mounted(): void {
    // Used to change image ratio given a MediaQuery Event
    window.addEventListener(
      WB_RESIZE_EVENT_NAME,
      this.resizeEventHandler as EventListenerOrEventListenerObject,
    );
    this.wbMediaQueryService.observe(true);
  },
  beforeDestroy(): void {
    window.removeEventListener(
      WB_RESIZE_EVENT_NAME,
      this.resizeEventHandler as EventListenerOrEventListenerObject,
    );
  },
  computed: {
    isEdit: function(): boolean {
      return componentMode.isEditMode;
    },
    title: function() {
      return this.isEdit ? this.componentData?.title : this.siteMapSection?.title;
    },
    links: function() {
      return this.isEdit ? this.componentData?.links : this.siteMapSection?.links;
    },
    visible: function() {
      return this.active;
    },
  },
  methods: {
    open() {
      this.active = !this.active;
    },
    start(el: HTMLElement) {
      el.style.height = el.scrollHeight + 'px';
    },
    end(el: HTMLElement) {
      el.style.height = '';
    },
    resizeEventHandler(resizeEvent: CustomEvent<MediaQueryChangeEvent>) {
      this.isMobile = [...mobileBreakpoints, ...tabletBreakpoints].includes(resizeEvent.detail.current);
    },
    handleTracking: function(linkUrl: string, linkLabel: string) {
      const eventLabel = this.getEventLabel(linkUrl, 'sitemap', linkLabel);
      this.handleCustomTracking(this.$el, eventLabel, 'link', 'click', 'navigation', linkUrl);
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/rtl-utils';

$siteMapSectionModuleName: 'owc-footer-sitemap-section';

.#{$siteMapSectionModuleName} {
  display: block;

  /*
  * TODO: This doesn't work with webcomponents and since the 
  * slotted pseudo element only allows simple-elements
  * we have to wait until a store is implemented to figure which
  * li is the last child
  */
  &:not(:last-child) {
    border-bottom: 1px solid var(--wb-grey-20);

    @include breakpoint-from(mq5) {
      border-bottom: none;
    }
  }
  &__title {
    @include wb-type-copy-strong;
  }
  &__title-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    padding: var(--wb-spacing-xs) 0;
    border: 0;

    color: inherit;
    font: inherit;
    text-align: initial;

    background: transparent;
    outline: 0;
    cursor: pointer;

    @include breakpoint-from(mq5) {
      min-height: 28px;
      margin-bottom: var(--wb-spacing-xs);
      padding: 0;

      pointer-events: none;
    }
  }
  &__expand-icon-wrapper {
    margin-right: 16px;
    margin-left: auto;

    @include breakpoint-from(mq5) {
      display: none;
    }

    &--expanded {
      .#{$siteMapSectionModuleName}__expand-icon {
        transform: rotate(180deg);
      }
    }
  }
  &__expand-icon {
    width: 16px;
    height: 16px;

    transition: transform 0.3s $wb-move;
  }
  &__link-list-wrapper {
    padding-bottom: var(--wb-spacing-s);

    @include breakpoint-from(mq5) {
      padding: 0;
    }
  }
  &__link {
    @include wb-type-copy-secondary;
    @include wb-link;
    @include wb-small-or-tiny-link;
    @include wb-small-or-tiny-link--theme-dark;
  }
  &__link-wrapper {
    &:first-child {
      padding-top: var(--wb-spacing-xxs);
    }
    &:not(:last-child) {
      // this is not xs because the 16px carry until desktop
      padding-bottom: 16px;
    }

    @include breakpoint-from(mq5) {
      &:first-child {
        padding-top: 0;
      }
    }
  }
}
.accordion-enter-active,
.accordion-leave-active {
  overflow: hidden;

  transition: height 0.65s $wb-move, opacity 0.5s 0.3s $wb-fade;

  @include breakpoint-from(mq5) {
    transition: none;
  }
}
.accordion-enter,
.accordion-leave-to {
  // this is needed to override the inline style added during the animation
  height: 0 !important;

  opacity: 0;
}

@include rtl-rule('.owc-footer-sitemap-section', false) {
  &__link-list {
    text-align: initial;
  }
}
</style>
